import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import IgnisIcon from '../components/icons/IgnisIcon';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useServedModel } from '../hooks/useServedModel';
import { IGNIS_MODELS, STATE_DATE, fmtInt, fmtPct1 } from '../lib/siteState';

// Public, curated explanation of the models behind IGNIS. Deliberately honest:
// it does NOT headline the inflated canonical AUC (~0.99, which measures
// susceptibility, not forecasting), and the 0.83 "honest" AUC published in 2026
// is retired (month-matched, same-event rows removed: 0.50 to 0.56). The meter
// is ignition coverage at 5 percent of the territory (docs/paper/REBUILD_NUMBERS.md).
// The tense keys on the served model family (country_runs.json): until the 7.3
// switch the rebuilt model runs in shadow and this page says so; after it the
// legacy model is the closed series (W5, docs/prod/SHIP_DECISION_7_3.md).

const M = IGNIS_MODELS;

function specFor(rebuiltServed: boolean): [string, string][] {
  const cov = M.rebuilt.coverage2026;
  if (rebuiltServed) {
    return [
      ['Modelo em serviço', `${M.rebuilt.version} (reconstruído em 2026)`],
      ['Algoritmo', 'LightGBM (gradient boosting) sobre o histórico de ignições por célula e mês'],
      ['Cobertura', 'Portugal continental, grelha de 1 km (88 527 células)'],
      ['Emissão', 'Uma por dia, de manhã: mapa de hoje e de amanhã'],
      ['Classes', 'Quotas fixas do território: Extremo 1%, Alto 2%, Moderado 5%'],
      ['Medida', `Ignições cobertas pelos 5% de células sinalizadas: ${fmtPct1(cov.rebuilt)} em 2026`],
      ['Referência', `Tabela histórica por célula e mês: ${fmtPct1(cov.lookup)}; modelo anterior: ${fmtPct1(cov.served)}`],
    ];
  }
  return [
    ['Modelo em serviço', `${M.served.version} (ensemble sazonal, congelado)`],
    ['Algoritmo', 'XGBoost + LightGBM por época do ano, probabilidades calibradas mês a mês'],
    ['Cobertura', 'Portugal continental, grelha de 1 km (88 527 células)'],
    ['Atualização', 'Mapa de hoje de 3 em 3 horas; previsão a 24 h de 6 em 6 horas'],
    ['Classes', 'Limiares de probabilidade: Baixo, Moderado, Alto, Extremo'],
    ['Medida', `Ignições cobertas pelos 5% de células sinalizadas: ${fmtPct1(cov.served)} em 2026`],
    ['Em sombra', `${M.rebuilt.version}: ${fmtPct1(cov.rebuilt)} na mesma medida; a correr em paralelo desde ${M.rebuilt.shadowSince}`],
  ];
}

function Section({ eyebrow, title, children }: { eyebrow?: string; title: string; children: ReactNode }) {
  return (
    <section className="border-t border-ink-800 py-10 first:border-t-0 first:pt-0">
      {eyebrow && <div className="mb-2 font-mono text-xs uppercase tracking-[0.22em] text-ember">{eyebrow}</div>}
      <h2 className="font-display text-2xl font-bold tracking-tight text-mist">{title}</h2>
      <div className="mt-4 space-y-4 text-mist-muted leading-relaxed">{children}</div>
    </section>
  );
}

function ShadowTable() {
  const s = M.rebuilt.shadow;
  const rows: [string, number, number, number][] = [
    [`Dias ao vivo (${s.liveDays} dias, ${fmtInt(s.liveFires)} ignições)`, s.live.served, s.live.lookup, s.live.rebuilt],
    [`Todos os dias avaliados (${s.allDays} dias, ${fmtInt(s.allFires)} ignições)`, s.all.served, s.all.lookup, s.all.rebuilt],
  ];
  return (
    <div className="overflow-x-auto rounded-xl border border-ink-700 bg-ink-900">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-ink-700 font-mono text-[11px] uppercase tracking-wider text-mist-faint">
            <th className="px-4 py-3">Ignições cobertas pelos 5% sinalizados</th>
            <th className="px-4 py-3">Modelo em serviço</th>
            <th className="px-4 py-3">Tabela histórica</th>
            <th className="px-4 py-3">Reconstruído</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, a, b, c]) => (
            <tr key={label} className="border-b border-ink-800 last:border-b-0">
              <td className="px-4 py-3 text-mist-muted">{label}</td>
              <td className="px-4 py-3 font-mono text-mist">{fmtPct1(a)}</td>
              <td className="px-4 py-3 font-mono text-mist">{fmtPct1(b)}</td>
              <td className="px-4 py-3 font-mono text-ember">{fmtPct1(c)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ModelComparison() {
  useDocumentMeta(
    'Os modelos por trás do IGNIS | Tutela',
    'Como funciona a previsão de risco de incêndio do IGNIS: o modelo em serviço, o modelo reconstruído em 2026 e a sua avaliação em sombra, as classes do mapa e como medimos o desempenho honestamente.',
    '/ignis/modelos',
  );
  const { rebuiltServed } = useServedModel();
  const cov = M.rebuilt.coverage2026;
  const s = M.rebuilt.shadow;
  const SPEC = specFor(rebuiltServed);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <div className="mx-auto max-w-3xl px-6 py-20">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-ember/30 bg-ember/10 px-4 py-1.5 font-mono text-sm font-semibold text-ember">
          <IgnisIcon className="h-4 w-4" />
          IGNIS · Modelos
        </div>

        <h1 className="font-display text-3xl font-bold tracking-tight text-mist sm:text-4xl">
          Os modelos por trás do IGNIS
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-mist-muted">
          O que produz o mapa de risco, como é treinado, e como medimos honestamente aquilo que
          consegue e não consegue fazer. O método de avaliação está descrito num preprint (não
          revisto por pares):{' '}
          <a
            href="https://doi.org/10.5281/zenodo.21793139"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-ember hover:brightness-110"
          >
            doi:10.5281/zenodo.21793139
          </a>
          .
        </p>

        {/* Spec sheet */}
        <div className="mt-10 rounded-xl border border-ink-700 bg-ink-900 p-6">
          <div className="mb-4 font-mono text-xs uppercase tracking-[0.2em] text-mist-faint">
            Ficha técnica · estado em {STATE_DATE}
          </div>
          <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
            {SPEC.map(([k, v]) => (
              <div key={k}>
                <dt className="font-mono text-[11px] uppercase tracking-wider text-mist-faint">{k}</dt>
                <dd className="mt-0.5 text-sm text-mist">{v}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="mt-12">
          {rebuiltServed ? (
            <Section title="O modelo">
              <p>
                O IGNIS não é uma caixa preta misteriosa. Parte de uma tabela que qualquer pessoa
                entende: com que frequência cada quadrícula de 1 km teve ignições em cada mês do ano,
                no registo do ICNF. Essa tabela, sozinha, já é uma referência forte, e é a primeira
                coisa que o modelo tem de bater.
              </p>
              <p>
                Por cima dela, um modelo de gradient boosting (LightGBM, centenas de árvores de decisão)
                aprende o desvio face a essa frequência histórica com a meteorologia do dia (previsão
                IFS, reanálise ERA5-Land, índices FWI) e as características do terreno. Calibração
                isotónica no ano anterior. O resultado é ordenado, não lido como probabilidade: as
                classes Extremo, Alto e Moderado são o 1, 2 e 5 por cento das quadrículas com maior
                risco nesse dia, todos os dias.
              </p>
            </Section>
          ) : (
            <Section title="O modelo em serviço">
              <p>
                O mapa público vem hoje de um ensemble sazonal: dois modelos de gradient boosting
                (XGBoost e LightGBM, internamente be23 e lst1) treinados sobre décadas de ignições do
                ICNF com cerca de 63 variáveis por célula-dia (meteorologia e reanálise, índices FWI,
                terreno, ocupação do solo, histórico de fogo, pressão humana). O sistema escolhe o modelo
                mais adequado a cada época do ano e calibra as probabilidades mês a mês. As classes do
                mapa são limiares sobre essa probabilidade.
              </p>
              <p>
                Este modelo está congelado desde a primavera de 2026 e é o que a página Validação avalia
                todos os dias desde 19 de maio. Ao reconstruir a avaliação em 2026, com todas as
                quadrículas do território em todos os dias de um ano não visto, cobre{' '}
                <span className="text-mist">{fmtPct1(cov.served)}</span> das ignições nos 5% de células
                que sinaliza, abaixo de uma simples tabela histórica por célula e mês ({fmtPct1(cov.lookup)}).
                É por isso que foi reconstruído.
              </p>
            </Section>
          )}

          {rebuiltServed ? (
            <Section title="O modelo anterior">
              <p>
                Até à troca, o mapa vinha de um ensemble sazonal (XGBoost e LightGBM, internamente be23 e
                lst1) que escolhia o modelo mais adequado a cada época e calibrava as probabilidades mês a
                mês. Ao reconstruir a avaliação em 2026, com todas as quadrículas do território em todos os
                dias de um ano não visto, esse modelo cobria {fmtPct1(cov.served)} das ignições nos 5% de
                células que sinalizava, abaixo da tabela histórica ({fmtPct1(cov.lookup)}). Foi por isso
                substituído.
              </p>
              <p>
                A sua série na Validação fica fechada no dia da troca, lado a lado com a do modelo novo e
                com a da tabela histórica, para que a comparação se possa ver.
              </p>
            </Section>
          ) : (
            <Section eyebrow="Em sombra" title="O modelo reconstruído (2026)">
              <p>
                Em 2026 reconstruímos o modelo a partir de uma tabela que qualquer pessoa entende: com que
                frequência cada quadrícula de 1 km teve ignições em cada mês do ano, no registo do ICNF.
                Essa tabela, sozinha, já é uma referência forte. Por cima dela, um modelo de gradient
                boosting (LightGBM) aprende o desvio face a essa frequência com a meteorologia do dia
                (previsão IFS, reanálise ERA5-Land, índices FWI) e as características do terreno, com
                calibração isotónica no ano anterior. O resultado é uma ordenação do território: as
                classes passam a ser quotas fixas, o 1, 2 e 5 por cento das quadrículas com maior risco
                nesse dia.
              </p>
              <p>
                Na avaliação de 2026 cobre <span className="text-mist">{fmtPct1(cov.rebuilt)}</span> das
                ignições nos 5% de células sinalizadas, contra {fmtPct1(cov.lookup)} da tabela histórica
                e {fmtPct1(cov.served)} do modelo em serviço.
              </p>
              <p>
                <span className="text-mist">Ainda não está no mapa.</span> Desde {M.rebuilt.shadowSince}{' '}
                emite um mapa todas as manhãs em paralelo com o modelo em serviço, sem o substituir, e cada
                mapa é pontuado contra as ignições reais desse dia, nos mesmos dias e contra os mesmos fogos
                que o modelo em serviço e a tabela histórica. A tabela abaixo é o estado dessa contagem em{' '}
                {STATE_DATE}:
              </p>
              <ShadowTable />
              <p className="text-sm">
                Diferença do reconstruído face à tabela histórica nos dias ao vivo: {s.rebuiltMinusLookup}{' '}
                (intervalo de reamostragem por dia). A troca só acontece se, com pelo menos{' '}
                {M.rebuilt.gate.liveDays} dias ao vivo e {fmtInt(M.rebuilt.gate.fires)} ignições, o modelo
                reconstruído continuar acima da tabela histórica; o mais cedo que isso pode ser lido é{' '}
                {M.rebuilt.gate.earliest}. Se não passar, não entra. Quando entrar, a série do modelo
                anterior fecha na Validação e a do novo começa nesse dia, lado a lado.
              </p>
            </Section>
          )}

          <Section eyebrow="Honestidade" title="Como medimos o desempenho">
            <p>
              Seria fácil mostrar um número impressionante. Numa avaliação ingénua, o modelo atinge uma
              AUC de cerca de 0.99. Mas esse número é enganador: compara zonas que ardem com zonas onde
              praticamente nunca há fogo, por isso mede sobretudo <span className="text-mist">onde</span> pode
              arder, e não <span className="text-mist">quando</span> vai arder.
            </p>
            <p>
              Em 2026 publicámos uma AUC "honesta" de 0,83, medida com casos difíceis. Ao reavaliar o
              mesmo modelo com negativos do mesmo mês e sem linhas do mesmo evento, o valor cai para{' '}
              <span className="text-mist">0,50 a 0,56</span>, pouco acima do acaso. Retirámos a
              afirmação em setembro de 2026. A medida que usamos agora não é uma AUC: é a fração das
              ignições de um ano não visto que cai nos 5% de quadrículas sinalizadas nesse dia, contando
              todas as quadrículas em todos os dias. Modelo reconstruído{' '}
              <span className="text-mist">{fmtPct1(cov.rebuilt)}</span>, tabela histórica {fmtPct1(cov.lookup)},
              modelo {rebuiltServed ? 'anterior' : 'em serviço'} {fmtPct1(cov.served)}.
            </p>
            <p>
              O mesmo rigor aplica-se ao mapa. {rebuiltServed ? 'Não mostra probabilidades: mostra uma' : 'O modelo reconstruído não mostra probabilidades: mostra uma'}{' '}
              <span className="text-mist">ordenação do território</span>, e a classe de cada célula
              diz onde ela fica nessa ordenação. A frequência com que cada classe arde de facto
              é publicada ao lado (em 2025, 0,30% das quadrículas-dia em Extremo, 0,15% em Alto, 0,08%
              em Moderado, 0,01% em Baixo), porque uma ignição num quilómetro quadrado específico num
              dia específico é um evento raríssimo. Publicamos essa medição, atualizada todas as
              noites, na página{' '}
              <Link to="/ignis/validacao" className="text-ember/80 underline-offset-2 hover:underline">
                Validação
              </Link>.
            </p>
          </Section>

          <Section title="O que testámos e não resultou">
            <p>
              Boa parte do trabalho foi descobrir o que não ajuda. Testámos, de forma controlada,
              índices de vegetação por satélite, humidade do combustível vivo, pressão de ignição
              humana e microtopografia de alta resolução, entre outros. Nenhum melhorou o modelo de
              forma significativa nos dados disponíveis. O ganho do modelo reconstruído vem da
              meteorologia do dia sobre o histórico, não de mais variáveis.
            </p>
            <p>
              Reportar estes resultados negativos é intencional. Diz-nos onde está o limite atual dos
              dados e evita promessas que não se sustentam.
            </p>
          </Section>

          <Section title="Limites e posicionamento">
            <p>
              O IGNIS é um complemento aos sistemas oficiais, como o IPMA e o EFFIS, e não um
              substituto. Em caso de emergência, as fontes oficiais prevalecem sempre.
            </p>
            <p>
              A nossa mais-valia é a resolução e a fusão de dados: uma leitura do risco célula a célula,
              a 1 km, dentro do mesmo concelho, a partir de muitas fontes públicas combinadas.
            </p>
          </Section>
        </div>

        <div className="mt-12 flex flex-wrap gap-4 border-t border-ink-800 pt-8">
          <Link
            to="/ignis/mapa-nacional"
            className="inline-flex items-center gap-2 rounded-lg bg-ember px-6 py-3 font-semibold text-ink-950 shadow-[0_0_30px_-8px_#F26A4B] transition-all duration-200 hover:brightness-110"
          >
            Ver o mapa nacional
          </Link>
          <Link
            to="/ignis"
            className="inline-flex items-center gap-2 rounded-lg px-5 py-3 font-mono text-sm uppercase tracking-widest text-mist-muted transition-colors hover:text-mist"
          >
            Voltar ao IGNIS
          </Link>
        </div>
      </div>
    </div>
  );
}
